import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppContext, RewardMethod } from "../../context/AppContext";
import { useCopy } from "../../context/CopyContext";
import { Ticket, CreditCard, Nfc } from "lucide-react";
import Header from "../Header";
import Heading from "../Heading";
import Button from "../Button";
import PoweredBy from "../PoweredBy";
import ItemsModalView from "./ItemsModalView";

// Helper function to transform empties data for display
const transformEmptiesForDisplay = (empties: any[]) => {
  return empties.map(empty => ({
    id: empty.product.id,
    name: empty.product.name,
    size: empty.product.size,
    quantity: empty.quantity,
    image: empty.product.image
  }));
};

// Helper function to get the icon component based on icon name
const getRewardIcon = (iconName: string) => {
  switch (iconName) {
    case 'CreditCard':
      return CreditCard;
    case 'Nfc':
      return Nfc;
    case 'Ticket':
      return Ticket;
    default:
      return Ticket;
  }
};

const SummaryView: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useAppContext();
  const { getCopy } = useCopy();
  const [showItemsModal, setShowItemsModal] = React.useState(false);

  const handleBack = () => {
    // If user is a guest, go back to membership selection
    // If user is a member, go back to reward method selection
    if (state.isGuest) {
      navigate("/membership-selection");
    } else {
      navigate("/reward-method-selection");
    }
  };

  const handleContinue = () => {
    console.log("User confirmed summary, checking product review setting");
    // Check if product review and scan is enabled
    if (state.settings.productReviewAndScan) {
      console.log("Product review and scan enabled, proceeding to rate returns screen");
      navigate("/rate-returns");
    } else {
      console.log("Product review and scan disabled, skipping to offer screen");
      navigate("/offer");
    }
  };

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  // Transform empties from state for display
  const displayEmpties = transformEmptiesForDisplay(state.empties);
  const totalQuantity = displayEmpties.reduce((sum, empty) => sum + empty.quantity, 0);
  
  // Calculate total rewards
  const totalRewards = state.empties.reduce((totals, empty) => {
    if (empty.product.isBranded && empty.product.rewards) {
      totals.points += empty.product.rewards.loyaltyPoints * empty.quantity;
      // Parse dollar value (remove $ and convert to number)
      const dollarAmount = parseFloat(empty.product.rewards.dollarValue.replace('$', ''));
      totals.dollars += dollarAmount * empty.quantity;
    }
    return totals;
  }, { points: 0, dollars: 0 });
  
  // Get selected reward method from state or fallback to default
  const getRewardInfo = (method: RewardMethod | undefined) => {
    switch (method) {
      case RewardMethod.LOYALTY_POINTS:
        return {
          name: getCopy('rewardMethodSelection.loyaltyPointsCard.title'),
          description: getCopy('rewardMethodSelection.loyaltyPointsCard.description'),
          value: `${totalRewards.points} pts`,
          icon: "CreditCard"
        };
      case RewardMethod.DIGITAL_CASH:
        return {
          name: getCopy('rewardMethodSelection.digitalCashCard.title'), 
          description: getCopy('rewardMethodSelection.digitalCashCard.description'),
          value: `$${totalRewards.dollars.toFixed(2)}`,
          icon: "Nfc"
        };
      case RewardMethod.DISCOUNT_COUPON:
      default:
        return {
          name: getCopy('rewardMethodSelection.discountCouponCard.title'),
          description: getCopy('rewardMethodSelection.discountCouponCard.description'),
          value: "20% OFF",
          icon: "Ticket"
        };
    }
  };
  
  const selectedReward = getRewardInfo(state.selectedRewardMethod);
  const RewardIcon = getRewardIcon(selectedReward.icon);

  return (
    <div
      className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden"
      data-node-id="164:4920"
    >
      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col gap-20">
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={state.itemsCount > 0}
              itemsNumber={state.itemsCount.toString()}
              emailAddress={state.userEmail}
              showEmailAddress={!!state.userEmail}
              isGuest={state.isGuest}
              isMember={!state.isGuest}
              animateIcon={true} // Animate icon since user reached summary after membership selection
              onItemsClick={handleItemsClick}
              selectedRewardValue={selectedReward.value}
            />
          </div>
        </div>

        {/* Summary Heading */}
        <div className="flex justify-center mx-20">
          <motion.div
            className="w-full max-w-[920px]"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ 
              duration: 0.6, 
              ease: [0.25, 0.1, 0.25, 1]
            }}
          >
            <Heading title={getCopy('summary.heading')} />
          </motion.div>
        </div>

        {/* Main Content Area */}
        <motion.div 
          className="flex-1 mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className="w-full max-w-[920px] mx-auto flex flex-col gap-10">
            
            {/* Reward Method Section */}
            <div className="flex flex-col gap-6">
              {/* Section Header */}
              <div className="flex flex-row items-start justify-between px-8 py-4 font-brand-primary font-medium text-[24px] tracking-[-0.24px] text-brand-text-primary">
                <div className="text-left w-[680px]">
                  <p className="leading-[32px]">{getCopy('summary.rewardMethodLabel')}</p>
                </div>
                <div className="text-right text-nowrap">
                  <p className="leading-[32px]">{getCopy('summary.totalRewardLabel')}</p>
                </div>
              </div>

              {/* Reward Card */}
              <div className="bg-brand-background border border-brand-border rounded-3xl px-8 py-6 flex flex-row gap-14 items-center justify-center w-full">
                <div className="flex-1 flex flex-row gap-8 items-center justify-center">
                  <div className="w-16 h-16 flex items-center justify-center">
                    <RewardIcon size={64} stroke="var(--brand-text-primary)" strokeWidth={1} />
                  </div>
                  <div className="flex-1 flex flex-col gap-2 text-left">
                    <h3 className="font-brand-primary font-normal text-[24px] leading-[31px] text-brand-text-primary">
                      {selectedReward.name}
                    </h3>
                    <p className="font-brand-secondary font-light text-[24px] leading-[31px] text-brand-text-muted">
                      {selectedReward.description}
                    </p>
                  </div>
                </div>
                {/* Display reward value based on selected method or guest status */}
                {state.isGuest ? (
                  // For guests, show discount percentage
                  <div className="bg-brand-tertiary rounded-full px-3 py-1 flex-shrink-0">
                    <span className="font-brand-primary font-medium text-[24px] leading-[31px] text-brand-background">
                      {state.empties.length > 0 ? 
                        Math.round(state.empties.reduce((avg, empty) => {
                          if (empty.product.isBranded && empty.product.rewards) {
                            return avg + (parseInt(empty.product.rewards.discountValue) * empty.quantity);
                          }
                          return avg;
                        }, 0) / state.empties.reduce((total, empty) => total + empty.quantity, 0)) : 0}% OFF
                    </span>
                  </div>
                ) : (
                  // For members, show value based on selected reward method
                  <div className="bg-brand-tertiary rounded-full px-3 py-1 flex-shrink-0">
                    <span className="font-brand-primary font-medium text-[24px] leading-[31px] text-brand-background">
                      {selectedReward.value}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Empties Section */}
            <div className="flex flex-col gap-4">
              {/* Section Header */}
              <div className="flex flex-row items-start justify-between px-8 py-4 font-brand-primary font-medium text-[24px] tracking-[-0.24px] text-brand-text-primary">
                <div className="flex-1 text-left">
                  <p className="leading-[32px]">{getCopy('summary.emptiesLabel')}</p>
                </div>
                <div className="text-right text-nowrap">
                  <p className="leading-[32px]">{getCopy('summary.quantityLabel')}</p>
                </div>
              </div>

              {/* Empties List */}
              <div className="flex flex-col gap-6">
                {displayEmpties.length > 0 ? displayEmpties.map((empty) => (
                  <div 
                    key={empty.id}
                    className="bg-brand-background border border-brand-border rounded-3xl pl-8 pr-10 py-6 flex flex-row gap-14 items-center justify-start w-full"
                  >
                    <div className="flex-1 flex flex-row gap-8 items-center justify-start">
                      {/* Product Image */}
                      <div 
                        className="w-16 h-16 bg-center bg-no-repeat bg-contain rounded-lg flex-shrink-0"
                        style={{ 
                          backgroundImage: `url('${empty.image}')`
                        }}
                      />
                      
                      {/* Product Info */}
                      <div className="flex-1 flex flex-col gap-2 text-left">
                        <h4 className="font-brand-primary font-normal text-[24px] leading-[31px] text-brand-text-primary overflow-ellipsis overflow-hidden text-nowrap">
                          {empty.name}
                        </h4>
                        <p className="font-brand-secondary font-light text-[24px] leading-[31px] text-brand-text-muted">
                          {empty.size}
                        </p>
                      </div>
                    </div>
                    
                    {/* Quantity */}
                    <div className="w-[93px] text-right">
                      <span className="font-brand-primary font-normal text-[24px] leading-[31px] text-brand-text-primary">
                        {empty.quantity}
                      </span>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8">
                    <p className="font-brand-secondary font-light text-[24px] leading-[31px] text-brand-text-muted">
                      {getCopy('summary.noEmptiesText')}
                    </p>
                  </div>
                )}
              </div>
            </div>


          </div>
        </motion.div>

        {/* Bottom Section - Back/Continue buttons and footer */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Buttons */}
          <div className="flex flex-row gap-10 w-full">
            <Button variant="secondary" onClick={handleBack}>
              {getCopy('summary.buttons.secondary')}
            </Button>
            <Button variant="primary" onClick={handleContinue}>
              {getCopy('summary.buttons.primary')}
            </Button>
          </div>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>

      {/* Items Modal */}
      <ItemsModalView 
        isOpen={showItemsModal} 
        onClose={handleCloseItemsModal} 
      />
    </div>
  );
};

export default SummaryView;
